import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HelpCircle } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { WhyScope } from '@/lib/why/types';
import { WhyDialog } from './why-dialog';

/**
 * The one entry point to the account, so Tower, Express and case-detail carry
 * IDENTICAL affordance and copy and each insertion point stays a single line.
 *
 * The dialog is MOUNTED ONLY WHILE OPEN: the read (and the room join it implies)
 * must not fire for every operator sitting on the Tower — the account is opened
 * deliberately, not subscribed to by default.
 *
 * Two visual weights, both the same control:
 *   · `link`   — inline, styled like the Tower's secondary link.
 *   · `chip`   — a bordered control that sits in a command bar / tab bar.
 */
export function WhyButton({
  scope,
  variant = 'link',
  className,
  testId = 'why-open',
}: {
  scope: WhyScope;
  variant?: 'link' | 'chip';
  className?: string;
  testId?: string;
}) {
  const { t } = useTranslation('why');
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={t('open.title')}
        aria-haspopup="dialog"
        className={cn(
          'inline-flex shrink-0 items-center gap-1 font-semibold',
          variant === 'link'
            ? 'self-start text-[11.5px] text-primary hover:underline'
            : 'h-7 rounded-md border border-border px-2 text-[11.5px] text-foreground hover:bg-muted',
          className,
        )}
        data-testid={testId}
      >
        <HelpCircle className="h-3 w-3 shrink-0" aria-hidden />
        {t('open.label')}
      </button>
      {open ? <WhyDialog scope={scope} onClose={() => setOpen(false)} /> : null}
    </>
  );
}
